import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Button, Icon } from 'semantic-ui-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import TeamStackedBarChart from './TeamStackedBarChart';
import TeamHeatmap from './TeamHeatmap';
import TeamLineChart from './TeamLineChart';

const VIEWS = [
    { key: 'weekly', label: 'Weekly Activity', icon: 'bar chart' },
    { key: 'calendar', label: 'Activity Calendar', icon: 'calendar' },
    { key: 'trends', label: 'Member Trends', icon: 'line graph' },
];

const VIEW_SUMMARIES = {
    weekly: (weeks) => `Weekly team practice sessions over the last ${weeks} weeks.`,
    calendar: (weeks) => `Daily practice activity for each team member over the last ${weeks} weeks.`,
    trends: (weeks) => `Individual practice frequency trends per team member over the last ${weeks} weeks.`,
};

const WEEK_OPTIONS = [4, 8, 12];

const TeamActivityCharts = ({ teamMembers, assessmentsStore, companyId, investorPitchEnabled }) => {
    const [activeView, setActiveView] = useState('weekly');
    const [weeks, setWeeks] = useState(12);
    const [collapsed, setCollapsed] = useState(false);
    const [exporting, setExporting] = useState(false);
    const chartRef = useRef(null);

    const renderOffscreenChart = useCallback((ChartComponent) => {
        return new Promise((resolve) => {
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '0';
            container.style.width = chartRef.current ? `${chartRef.current.offsetWidth}px` : '800px';
            container.style.backgroundColor = '#ffffff';
            document.body.appendChild(container);

            ReactDOM.render(
                <ChartComponent teamMembers={teamMembers} assessmentsStore={assessmentsStore} weeks={weeks} />,
                container,
                async () => {
                    // Small delay for recharts to finish rendering
                    await new Promise(r => setTimeout(r, 500));
                    const canvas = await html2canvas(container, {
                        backgroundColor: '#ffffff',
                        scale: 2,
                        useCORS: true,
                    });
                    const img = canvas.toDataURL('image/png');
                    ReactDOM.unmountComponentAtNode(container);
                    document.body.removeChild(container);
                    resolve({ img, width: canvas.width, height: canvas.height });
                }
            );
        });
    }, [teamMembers, assessmentsStore, weeks]);

    const buildSinglePagePDF = useCallback((pdf, captures) => {
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // Teal header bar
        pdf.setFillColor(19, 127, 162);
        pdf.rect(0, 0, pageWidth, 18, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.setTextColor(255, 255, 255);
        pdf.text('MULTIPLY', 10, 12);

        // Teal footer bar
        pdf.setFillColor(19, 127, 162);
        pdf.rect(0, pageHeight - 12, pageWidth, 12, 'F');
        pdf.setFontSize(9);
        pdf.setTextColor(255, 255, 255);
        pdf.text('askmultiply.com', pageWidth / 2, pageHeight - 4, { align: 'center' });

        // Page background
        pdf.setFillColor(235, 235, 235);
        pdf.rect(0, 18, pageWidth, pageHeight - 30, 'F');

        // Title
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.setTextColor(44, 62, 80);
        pdf.text('Team Practice Activity Report', pageWidth / 2, 30, { align: 'center' });
        const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Generated: ${dateStr}`, pageWidth / 2, 36, { align: 'center' });

        // Layout: 3 charts stacked vertically
        const margin = 10;
        const availableWidth = pageWidth - margin * 2;
        const startY = 41;
        const footerY = pageHeight - 14;
        const totalHeight = footerY - startY;
        const labelHeight = 8; // space for label above each chart
        const gap = 3;
        const chartAreaHeight = (totalHeight - gap * 2 - labelHeight * 3) / 3;

        captures.forEach((cap, i) => {
            const sectionY = startY + i * (chartAreaHeight + labelHeight + gap);

            // View label
            const view = VIEWS.find(v => v.key === cap.key);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.setTextColor(44, 62, 80);
            pdf.text(view.label, margin, sectionY + 4);

            const summary = VIEW_SUMMARIES[cap.key](weeks);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7);
            pdf.setTextColor(100, 100, 100);
            pdf.text(summary, margin + 45, sectionY + 4);

            // Chart image
            const chartTop = sectionY + labelHeight;
            const imgAspect = cap.height / cap.width;
            const imgWidth = availableWidth;
            const imgHeight = imgWidth * imgAspect;
            const finalHeight = Math.min(imgHeight, chartAreaHeight);
            const finalWidth = imgHeight > chartAreaHeight ? finalHeight / imgAspect : imgWidth;

            pdf.addImage(cap.img, 'PNG', margin + (availableWidth - finalWidth) / 2, chartTop, finalWidth, finalHeight);
        });
    }, [weeks]);

    const handleDownloadPDF = useCallback(async () => {
        if (exporting) return;
        setExporting(true);

        try {
            const chartComponents = [
                { key: 'weekly', Component: TeamStackedBarChart },
                { key: 'calendar', Component: TeamHeatmap },
                { key: 'trends', Component: TeamLineChart },
            ];

            // Capture the currently visible chart directly, render others offscreen
            const captures = [];
            for (const { key, Component } of chartComponents) {
                if (key === activeView && chartRef.current) {
                    const canvas = await html2canvas(chartRef.current, {
                        backgroundColor: '#ffffff',
                        scale: 2,
                        useCORS: true,
                    });
                    captures.push({ key, img: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height });
                } else {
                    const result = await renderOffscreenChart(Component);
                    captures.push({ key, ...result });
                }
            }

            const pdf = new jsPDF('p', 'mm', 'a4');
            buildSinglePagePDF(pdf, captures);

            const dateFile = new Date().toISOString().split('T')[0];
            pdf.save(`team-activity-report-${dateFile}.pdf`);
        } catch (err) {
            console.error('PDF export failed:', err);
        } finally {
            setExporting(false);
        }
    }, [activeView, weeks, exporting, renderOffscreenChart, buildSinglePagePDF]);

    return (
        <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            padding: collapsed ? '12px 20px' : '20px',
            marginBottom: '20px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
        }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsed ? 0 : '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setCollapsed(!collapsed)}>
                    <Icon name={collapsed ? 'chevron right' : 'chevron down'} style={{ color: '#137fa2', fontSize: '12px' }} />
                    <span style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#137fa2' }}>
                        Team Activity
                    </span>
                </div>

                {!collapsed && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {/* Time range */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {WEEK_OPTIONS.map(w => (
                                <button
                                    key={w}
                                    onClick={() => setWeeks(w)}
                                    style={{
                                        padding: '3px 8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '10px',
                                        fontFamily: 'Montserrat',
                                        cursor: 'pointer',
                                        backgroundColor: weeks === w ? '#137fa2' : 'white',
                                        color: weeks === w ? 'white' : '#666',
                                        fontWeight: weeks === w ? 600 : 400
                                    }}
                                >
                                    {w}w
                                </button>
                            ))}
                        </div>

                        {/* View tabs */}
                        <Button.Group size="mini">
                            {VIEWS.map(v => (
                                <Button
                                    key={v.key}
                                    icon={v.icon}
                                    title={v.label}
                                    active={activeView === v.key}
                                    onClick={() => setActiveView(v.key)}
                                    style={{
                                        backgroundColor: activeView === v.key ? '#137fa2' : '#f0f0f0',
                                        color: activeView === v.key ? 'white' : '#666'
                                    }}
                                />
                            ))}
                        </Button.Group>

                        {/* PDF Download */}
                        <Button
                            icon="file pdf outline"
                            size="mini"
                            title="Download as PDF"
                            loading={exporting}
                            disabled={exporting}
                            onClick={handleDownloadPDF}
                            style={{ backgroundColor: '#137fa2', color: 'white' }}
                        />
                    </div>
                )}
            </div>

            {/* Chart content */}
            {!collapsed && (
                <div ref={chartRef}>
                    {activeView === 'weekly' && (
                        <TeamStackedBarChart teamMembers={teamMembers} assessmentsStore={assessmentsStore} companyId={companyId} weeks={weeks} investorPitchEnabled={investorPitchEnabled} />
                    )}
                    {activeView === 'calendar' && (
                        <TeamHeatmap teamMembers={teamMembers} assessmentsStore={assessmentsStore} companyId={companyId} weeks={weeks} />
                    )}
                    {activeView === 'trends' && (
                        <TeamLineChart teamMembers={teamMembers} assessmentsStore={assessmentsStore} companyId={companyId} weeks={weeks} />
                    )}
                </div>
            )}
        </div>
    );
};

export default TeamActivityCharts;
